import { FC, Suspense, useCallback, useContext, useState } from 'react';
import { useLocation } from 'react-router';
import { Add } from '@mui/icons-material';
import { Button, Stack, Typography, useMediaQuery } from '@mui/material';

import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { DialogContext } from '@cvt/contexts/DialogContext/DialogContext';
import { useDictionary } from '@cvt/hooks/useDictionary';

import { BoxWell } from '@shared/components/BoxWell';

import { StoriesFilters, Props as StoriesFiltersProps } from '../partials/StoriesFilters';
import { StoryItemSkeleton } from '../components/StoryItemSkeleton';
import { StoryList } from '../partials/StoryList';
import { StoryListItem } from '../components/StoryListItem';
import { useStoryCrud } from '../hooks/useStoryCrud';


export interface Props extends StoriesFiltersProps {
}

export const ViewStories: FC<Props> = (props) => {
  const { filters, setters, disabledFilters } = props;
  const [storiesMetadata, setStoriesMetadata] = useState<CVT.Query.Metadata | null>(null);
  const router = useRouter();
  const location = useLocation();
  const dictionary = useDictionary();
  const { asyncConfirmation } = useContext(DialogContext);

  const { deleteStory } = useStoryCrud();

  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const onDelete = useCallback(async (id: number) => {
    const confirmed = await asyncConfirmation({ title: dictionary.stories.edit.deleteConfirmation });
    if (!confirmed) {
      return false;
    }
    try {
      await deleteStory.mutateAsync(id);
    } catch (error) {
      console.error(error);
    }
  }, [asyncConfirmation, dictionary, deleteStory]);

  return (
    <Stack gap={2} width="100%">
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center" justifyContent="space-between">
        <Stack spacing={2}>
          <Typography variant="h4" component="h1" fontWeight={600} fontSize={{ xs: 24, sm: 30 }} textAlign={{ xs: 'center', sm: 'left' }}>
            Your Stories
          </Typography>
          <Typography variant="body1" fontSize={16} color="text.secondary" fontWeight={500} textAlign={{ xs: 'center', sm: 'left' }}>
            Craft and refine your leadership narratives with AI-powered insights
          </Typography>
        </Stack>
        <Button
          variant="contained"
          color="primary"
          onClick={() => router.stories.create.go()}
          startIcon={<Add/>}
          size="large"
          fullWidth={isMobile}
        >
          Create New Story
        </Button>
      </Stack>

      <BoxWell>
        <StoriesFilters
          filters={filters}
          setters={setters}
          disabledFilters={disabledFilters}
          storiesMetadata={storiesMetadata}
        />
      </BoxWell>

      <ErrorBoundary>
        <Suspense
          key={location.key}
          fallback={(
            <StoryItemSkeleton/>
          )}
        >
          <StoryList
            filters={filters}
            renderItem={story => <StoryListItem key={story.id} {...story} onDelete={onDelete}/>}
            onMetadataChange={setStoriesMetadata}
          />
        </Suspense>
      </ErrorBoundary>
    </Stack>
  );
};
